<template>
  <ProductCardCarouselSkeleton v-if="pending" :with-title="!!title" />
  <section
    v-else-if="products.length > 0"
    data-qa="recommendation-carousel"
    data-test="recommendation-carousel"
  >
    <ProductCardCarousel
      :cta
      :products="productsWithDiscountedPrice"
      :subtitle
      :title
      :tracking="{ ...trackingData, provider: 'attraqt' }"
      :use-client-side-navigation
      v-on="options.withAddToCart ? { addToCart: addToCartHandler } : {}"
    >
      <template #post-card="{ product, index }">
        <slot :index name="post-card" :product />
      </template>
    </ProductCardCarousel>
  </section>
</template>

<script lang="ts" setup>
import { watch } from 'vue'

import type { CTALink } from '@backmarket/http-api/src/api-specs-content/models/cta-link'
import { useDiscountedPrice } from '@backmarket/nuxt-layer-buyback/composables/buybackOffer/useDiscountedPrice'

import type { ExtraTrackingData } from '../composables/useProductTracking'
import { useRecommendedProducts } from '../composables/useRecommendation'

import ProductCardCarousel from './ProductCardCarousel/ProductCardCarousel.vue'
import ProductCardCarouselSkeleton from './ProductCardCarousel/ProductCardCarouselSkeleton.vue'

const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    cta?: CTALink
    recommendationQuery: {
      limit: number
      personalisation?: boolean
    } & (
      | {
          scope: string
          scopeId: string
          category: string
        }
      | {
          widgetId: string
        }
    )
    options?: {
      withCrossedPrice?: boolean
      withStartingFrom?: boolean
      withGrade?: boolean
      withAddToCart?: boolean
    }
    trackingData?: ExtraTrackingData
    useClientSideNavigation?: boolean
  }>(),
  {
    title: undefined,
    subtitle: undefined,
    cta: undefined,
    options: () => ({
      withCrossedPrice: false,
      withStartingFrom: true,
      withGrade: false,
      withAddToCart: false,
    }),
    trackingData: () => ({}),
    useClientSideNavigation: true,
  },
)

const {
  products,
  pending,
  error,
  status: requestStatus,
} = useRecommendedProducts(props.recommendationQuery)

const { products: productsWithDiscountedPrice } = useDiscountedPrice(products)

const emit = defineEmits(['error', 'loaded', 'refresh', 'added'])

watch(products, () => {
  // avoid emitting an error if we failed once and we are re-fetching
  if (requestStatus.value === 'pending') return

  if (products.value.length === 0) {
    emit('error', null)
  } else {
    emit('loaded', products.value)
  }
})

watch(error, () => {
  // avoid emitting an error if we failed once and we are re-fetching
  if (requestStatus.value === 'pending') return

  if (error.value !== null) {
    emit('error', error)
  }
})

function addToCartHandler({
  status,
  product,
}: {
  status: 'success' | 'error'
  product: { listingId: string }
}) {
  if (status === 'success') {
    emit('refresh', product.listingId)
    emit('added', product)
  }
}
</script>
